import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Mail, Send, CheckCircle2, Clock, XCircle, RotateCcw, Loader2, AlertCircle, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiErrorMessage } from "@/lib/api";
import TripEmailComposer from "@/pages/trips/TripEmailComposer";
import { ConnectEmailDialog } from "@/pages/trips/AsistenteTab";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("es-ES") : "—");

const ESTADOS = {
  no_solicitada: { label: "No solicitada", icon: XCircle, cls: "border-zinc-200 bg-zinc-50 text-zinc-600" },
  solicitada_no_recibida: { label: "Solicitada · sin recibir", icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-700" },
  solicitada_recibida: { label: "Solicitada y recibida", icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
};

export function SolicitudFacturaReserva({ tripId, reservaId, onChanged }) {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(todayISO());
  const [composer, setComposer] = useState(false);
  const [showConnect, setShowConnect] = useState(false);

  const stQ = useQuery({ queryKey: ["sf-reserva", reservaId], queryFn: async () => (await api.get(`/solicitud-factura/reserva/${reservaId}`)).data });
  const st = stQ.data;

  const refresh = () => { qc.invalidateQueries({ queryKey: ["sf-reserva", reservaId] }); onChanged?.(); };

  const solicitarMut = useMutation({
    mutationFn: async () => (await api.post(`/solicitud-factura/reserva/${reservaId}/solicitar`, { fecha })).data,
    onSuccess: () => { toast.success("Factura solicitada"); refresh(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const recibidaMut = useMutation({
    mutationFn: async (recibida) => (await api.post(`/solicitud-factura/reserva/${reservaId}/recibida`, { recibida })).data,
    onSuccess: (_, recibida) => { toast.success(recibida ? "Marcada como recibida" : "Marcada como pendiente"); refresh(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancelarMut = useMutation({
    mutationFn: async () => (await api.post(`/solicitud-factura/reserva/${reservaId}/cancelar`)).data,
    onSuccess: () => { toast.success("Solicitud anulada"); refresh(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (stQ.isLoading || !st) return <Skeleton className="h-48 w-full" />;

  if (st.is_monthly) {
    return (
      <Card><CardContent className="flex items-center gap-3 p-5 text-sm text-muted-foreground" data-testid="sf-reserva-monthly">
        <Calendar className="h-5 w-5 text-amber-500" />
        Este proveedor se factura de forma <strong>mensual</strong>. La factura se gestiona desde el módulo de Factura Mensual, no por solicitud individual.
      </CardContent></Card>
    );
  }

  const e = ESTADOS[st.estado] || ESTADOS.no_solicitada;
  const Icon = e.icon;

  return (
    <div className="space-y-4" data-testid="sf-reserva-tab">
      <Card><CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`gap-1 ${e.cls}`} data-testid="sf-reserva-estado"><Icon className="h-3.5 w-3.5" />{e.label}</Badge>
            {st.estado === "no_solicitada" && st.recibida && <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />recibida</Badge>}
            {st.provider_name && <span className="text-sm text-muted-foreground">· {st.provider_name}</span>}
          </div>
          {st.solicitada && (
            <span className="text-xs text-muted-foreground" data-testid="sf-reserva-info">
              Solicitada el <strong>{fmtD(st.solicitada_at)}</strong> por <strong>{st.solicitada_by || "—"}</strong>
            </span>
          )}
        </div>

        {!st.solicitada ? (
          <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/20 p-3">
            <div className="space-y-1">
              <Label className="text-xs">Fecha de solicitud</Label>
              <Input type="date" value={fecha} onChange={(ev) => setFecha(ev.target.value)} className="h-9 w-[170px]" data-testid="sf-reserva-fecha" />
            </div>
            <Button onClick={() => solicitarMut.mutate()} disabled={solicitarMut.isPending} data-testid="sf-reserva-solicitar">
              {solicitarMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}Solicitar factura
            </Button>
            {st.provider_email && (
              <Button variant="outline" onClick={() => setComposer(true)} data-testid="sf-reserva-email">
                <Send className="mr-1.5 h-4 w-4" />Solicitar por email
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {!st.recibida && (
              <Button variant="outline" className="text-emerald-700" onClick={() => recibidaMut.mutate(true)} disabled={recibidaMut.isPending} data-testid="sf-reserva-recibida">
                <CheckCircle2 className="mr-1.5 h-4 w-4" />Marcar factura recibida
              </Button>
            )}
            {st.recibida && (
              <Button variant="outline" onClick={() => recibidaMut.mutate(false)} disabled={recibidaMut.isPending} data-testid="sf-reserva-pendiente">
                <Clock className="mr-1.5 h-4 w-4" />Marcar pendiente
              </Button>
            )}
            {st.provider_email && (
              <Button variant="outline" onClick={() => setComposer(true)} data-testid="sf-reserva-email-2">
                <Send className="mr-1.5 h-4 w-4" />Reenviar por email
              </Button>
            )}
            <Button variant="ghost" className="text-muted-foreground" onClick={() => cancelarMut.mutate()} disabled={cancelarMut.isPending} data-testid="sf-reserva-cancelar">
              <RotateCcw className="mr-1.5 h-4 w-4" />Anular solicitud
            </Button>
          </div>
        )}

        <div className="border-t pt-3 text-xs text-muted-foreground">
          {st.provider_email
            ? <>Proveedor con email: <strong>{st.provider_email}</strong>. {st.email_connected ? <>Se enviará desde <strong>{st.sender_email}</strong>.</> : <button className="text-primary underline" onClick={() => setShowConnect(true)}>Conecta tu correo</button>}</>
            : "Este proveedor no tiene email configurado: registra la solicitud manualmente con la fecha."}
        </div>
      </CardContent></Card>

      {!st.email_connected && st.provider_email && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4" /> Conecta tu correo para enviar la solicitud por email.
          <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => setShowConnect(true)}><Mail className="mr-1.5 h-3.5 w-3.5" />Conectar</Button>
        </div>
      )}

      {composer && (
        <TripEmailComposer open={composer} onClose={(refreshIt) => { setComposer(false); if (refreshIt) refresh(); }}
          tripId={tripId} providerId={st.provider_id} reservaIds={[reservaId]} toEmail={st.provider_email}
          providerName={st.provider_name} multi={false} emailConnected={st.email_connected} senderEmail={st.sender_email}
          apiBase="/solicitud-factura" titleSingle="Solicitar factura al proveedor" />
      )}

      <ConnectEmailDialog open={showConnect} onClose={() => setShowConnect(false)} currentEmail={st.sender_email}
        onConnected={refresh} />
    </div>
  );
}
